import { localize, type AssistMirror, type KioskAssistCall, type Locale } from '@360face/domain';
import type { V2State } from './flow';

/**
 * What the operator sees of the kiosk while the call is connected (owner 20.10.2026): the step,
 * the chosen treatment, day, time, therapist and the error on the screen. Never the phone, name,
 * e-mail or the health form (the server also rejects any other field).
 */
export function assistMirror(state: V2State, staffName: (id?: string) => string): AssistMirror {
  const m: AssistMirror = { step: state.step, mode: state.mode, locale: state.locale };
  if (state.service) m.service = localize(state.service.title, state.locale).slice(0, 80);
  if (state.date) m.date = state.date;
  if (state.slot) {
    m.date = state.slot.start.slice(0, 10);
    m.time = state.slot.start.slice(11, 16);
    const who = staffName(state.slot.staffId);
    if (who) m.staff = who.slice(0, 60);
  }
  const extras = state.addonIds.length + state.productIds.length;
  if (extras) m.extras = Math.min(extras, 20);
  if (state.notice && /^[A-Z_]{1,40}$/.test(state.notice)) m.notice = state.notice;
  return m;
}

/** What the call overlay shows. */
export type AssistView =
  | { kind: 'starting' }
  | { kind: 'ringing'; stage: KioskAssistCall['stage'] }
  | { kind: 'active'; src: string; operatorName?: string }
  | { kind: 'fallback' }
  | { kind: 'ended' };

/** Overlay state from the server answer (or a failed request). */
export function assistView(call: KioskAssistCall | undefined, failed: boolean): AssistView {
  if (failed) return { kind: 'fallback' };
  if (!call) return { kind: 'starting' };
  if (call.status === 'active' && call.join) {
    return {
      kind: 'active',
      src: `${call.join.roomUrl}?t=${encodeURIComponent(call.join.token)}`,
      operatorName: call.operatorName,
    };
  }
  if (call.status === 'ended') return { kind: 'ended' };
  if (call.status === 'missed' || !call.video || call.stage === 'whatsapp') return { kind: 'fallback' };
  return { kind: 'ringing', stage: call.stage };
}

/** Seconds left before the limit, or undefined while there is no limit. */
export function secondsLeft(endsAt: string | undefined, now: number): number | undefined {
  if (!endsAt) return undefined;
  return Math.max(0, Math.round((Date.parse(endsAt) - now) / 1000));
}

export function mmss(sec: number): string {
  return `${Math.floor(sec / 60)}:${String(sec % 60).padStart(2, '0')}`;
}

/** wa.me link of the studio WhatsApp (E.164 without "+"). */
export function whatsappLink(e164: string): string {
  return `https://wa.me/${e164.replace(/\D/g, '')}`;
}

/** "+351 915 311 522" for reading. */
export function prettyPhone(e164: string): string {
  const d = e164.replace(/\D/g, '');
  if (d.startsWith('351') && d.length === 12) return `+351 ${d.slice(3, 6)} ${d.slice(6, 9)} ${d.slice(9)}`;
  return `+${d}`;
}

export interface AssistMessages {
  title: string;
  calling: string;
  callingSub: string;
  tablets: string;
  fallbackTitle: string;
  fallbackSub: string;
  retry: string;
  cancel: string;
  close: string;
  end: string;
  connected: (name?: string) => string;
  endsIn: (t: string) => string;
  ended: string;
  camera: string;
}

export const ASSIST_MESSAGES: Record<Locale, AssistMessages> = {
  pt: {
    title: 'Já vamos ajudar!',
    calling: 'A ligar à nossa equipa…',
    callingSub: 'Normalmente atendemos em menos de um minuto.',
    tablets: 'Também avisámos a equipa do estúdio.',
    fallbackTitle: 'Não conseguimos atender agora',
    fallbackSub: 'Escreva-nos no WhatsApp — leia o código com o seu telemóvel.',
    retry: 'Tentar de novo',
    cancel: 'Cancelar',
    close: 'Fechar',
    end: 'Terminar',
    connected: (n) => (n ? `Está a falar com ${n}` : 'Ligado'),
    endsIn: (t) => `A chamada termina em ${t}`,
    ended: 'Chamada terminada. Obrigado!',
    camera: 'A câmara e o microfone são usados só durante a chamada. Nada é gravado.',
  },
  en: {
    title: 'We’ll help you shortly',
    calling: 'Calling our team…',
    callingSub: 'We usually answer in under a minute.',
    tablets: 'We have also alerted the studio team.',
    fallbackTitle: 'We can’t answer right now',
    fallbackSub: 'Message us on WhatsApp — scan the code with your phone.',
    retry: 'Try again',
    cancel: 'Cancel',
    close: 'Close',
    end: 'End call',
    connected: (n) => (n ? `You are talking to ${n}` : 'Connected'),
    endsIn: (t) => `The call ends in ${t}`,
    ended: 'Call ended. Thank you!',
    camera: 'Camera and microphone are used only during the call. Nothing is recorded.',
  },
  fr: {
    title: 'Nous allons vous aider',
    calling: 'Appel de notre équipe…',
    callingSub: 'Nous répondons généralement en moins d’une minute.',
    tablets: 'Nous avons aussi prévenu l’équipe du studio.',
    fallbackTitle: 'Nous ne pouvons pas répondre pour le moment',
    fallbackSub: 'Écrivez-nous sur WhatsApp — scannez le code avec votre téléphone.',
    retry: 'Réessayer',
    cancel: 'Annuler',
    close: 'Fermer',
    end: 'Raccrocher',
    connected: (n) => (n ? `Vous parlez avec ${n}` : 'Connecté'),
    endsIn: (t) => `L’appel se termine dans ${t}`,
    ended: 'Appel terminé. Merci !',
    camera: 'La caméra et le micro ne servent que pendant l’appel. Rien n’est enregistré.',
  },
  es: {
    title: 'Enseguida te ayudamos',
    calling: 'Llamando a nuestro equipo…',
    callingSub: 'Normalmente respondemos en menos de un minuto.',
    tablets: 'También hemos avisado al equipo del estudio.',
    fallbackTitle: 'No podemos atender ahora',
    fallbackSub: 'Escríbenos por WhatsApp — escanea el código con tu móvil.',
    retry: 'Intentar de nuevo',
    cancel: 'Cancelar',
    close: 'Cerrar',
    end: 'Colgar',
    connected: (n) => (n ? `Estás hablando con ${n}` : 'Conectado'),
    endsIn: (t) => `La llamada termina en ${t}`,
    ended: 'Llamada terminada. ¡Gracias!',
    camera: 'La cámara y el micrófono solo se usan durante la llamada. No se graba nada.',
  },
  ru: {
    title: 'Мы скоро вам поможем',
    calling: 'Звоним нашей команде…',
    callingSub: 'Обычно отвечаем меньше чем за минуту.',
    tablets: 'Мы также предупредили мастеров в студии.',
    fallbackTitle: 'Сейчас не можем ответить',
    fallbackSub: 'Напишите нам в WhatsApp — наведите камеру телефона на код.',
    retry: 'Попробовать снова',
    cancel: 'Отмена',
    close: 'Закрыть',
    end: 'Завершить',
    connected: (n) => (n ? `Вы говорите с ${n}` : 'Соединено'),
    endsIn: (t) => `Звонок закончится через ${t}`,
    ended: 'Звонок завершён. Спасибо!',
    camera: 'Камера и микрофон работают только во время звонка. Ничего не записывается.',
  },
  uk: {
    title: 'Ми скоро вам допоможемо',
    calling: 'Телефонуємо нашій команді…',
    callingSub: 'Зазвичай відповідаємо менш ніж за хвилину.',
    tablets: 'Ми також попередили майстрів у студії.',
    fallbackTitle: 'Зараз не можемо відповісти',
    fallbackSub: 'Напишіть нам у WhatsApp — наведіть камеру телефона на код.',
    retry: 'Спробувати знову',
    cancel: 'Скасувати',
    close: 'Закрити',
    end: 'Завершити',
    connected: (n) => (n ? `Ви говорите з ${n}` : 'З’єднано'),
    endsIn: (t) => `Дзвінок завершиться через ${t}`,
    ended: 'Дзвінок завершено. Дякуємо!',
    camera: 'Камера й мікрофон працюють лише під час дзвінка. Нічого не записується.',
  },
  de: {
    title: 'Wir helfen Ihnen gleich',
    calling: 'Wir rufen unser Team an…',
    callingSub: 'Wir antworten meist in unter einer Minute.',
    tablets: 'Wir haben auch das Studio-Team informiert.',
    fallbackTitle: 'Wir können gerade nicht antworten',
    fallbackSub: 'Schreiben Sie uns auf WhatsApp — scannen Sie den Code mit Ihrem Handy.',
    retry: 'Erneut versuchen',
    cancel: 'Abbrechen',
    close: 'Schließen',
    end: 'Auflegen',
    connected: (n) => (n ? `Sie sprechen mit ${n}` : 'Verbunden'),
    endsIn: (t) => `Der Anruf endet in ${t}`,
    ended: 'Anruf beendet. Danke!',
    camera: 'Kamera und Mikrofon werden nur während des Anrufs genutzt. Nichts wird aufgezeichnet.',
  },
};
